import { useEffect, useState } from "react"
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Button as PriButton
} from "@mui/material"
import Button from "../Button/Button"
import { Wrapper, HeaderWrapper, Header, Subheader } from "./ManageTeams.styles"
import { WorkQueuesRead } from "../../api/generated/models/WorkQueuesRead"
import DeleteModal from "../DeleteModal/DeleteModal"
import { TeamsDetailRead, UserRead } from "../../api/generated"
import { Add } from "@mui/icons-material"
// @ts-ignore
import DeleteIcon from "../../assets/Trash.svg?react"
import { useAppSelector } from "../../app/hooks"
import { selectLGInternalUser } from "../../activeScreenSlice"

export interface Props {
  teams: Array<TeamsDetailRead>
  teamLeads: Array<UserRead>
  workLists: Array<WorkQueuesRead>
  onCreate: (team: TeamsDetailRead) => any
  onDelete: (team: TeamsDetailRead) => any
  onUpdate: (team: {
    id: number
    name: string
    teamLeadId: number
    workqueueIds: number[]
  }) => any
}

// Keep track of non submitted teams so can instantly delete without modal etc
const clientOnlyTeams: Record<string, boolean> = {}

export default function ManageTeams({
  teamLeads,
  workLists,
  onCreate,
  onUpdate,
  onDelete,
  ...props
}: Props) {
  const [teamToDelete, setTeamToDelete] = useState<TeamsDetailRead | null>(null)
  const LGInternalUser = useAppSelector(selectLGInternalUser);

  const [teams, setTeams] = useState<Array<TeamsDetailRead>>(props.teams)
  useEffect(() => {
    setTeams((teams) =>
      props.teams.map((team) => teams.find(({ id }) => id === team.id) || team),
    )
  }, [props.teams])

  function updateTeam(team: TeamsDetailRead, patch?: {}) {
    setTeams((prevTeams) =>
      prevTeams.map((prevItem) =>
        prevItem.id === team.id ? { ...prevItem, ...patch } : prevItem,
      ),
    )
  }

  function addTeam() {
    const id = Date.now()

    clientOnlyTeams[id] = true

    setTeams((prevTeams) =>
      // @ts-ignore
      prevTeams.concat({
        id,
        name: "",
        team_lead: { id: "" },
        workqueues: [],
      }),
    )
  }

  function deleteTeam(team: TeamsDetailRead) {
    setTeams((prevTeams) =>
      prevTeams.filter((prevTeam) => prevTeam.id !== team.id),
    )
  }

  function handleUpdate(
    team: TeamsDetailRead,
    partial?:
      | { name: string }
      | { teamLeadId: number }
      | { defaultWorkListId: number },
  ) {
    if (clientOnlyTeams[team.id]) {
      updateTeam(team, partial)
      return
    }

    onUpdate({
      id: team.id,
      name: team.name || "",
      teamLeadId: team.team_lead.id,
      workqueueIds: team.workqueues.map((queue) => queue.id),
      ...partial,
    })
  }

  function handleTeamSelection(
    team: TeamsDetailRead,
    event: SelectChangeEvent<number[]>,
  ) {
    if (typeof event.target.value === "string") {
      return
    }
    const queues = event.target.value.map((queueId: number) => ({
      id: queueId,
    }))
    updateTeam(team, {
      workqueues: queues,
    })
  }

  return (
    <Wrapper>
      <DeleteModal
        open={Boolean(teamToDelete)}
        header={`You are about to delete team ${teamToDelete?.name}`}
        subheader="All team members will be unassigned, and need to be assigned to
        another team"
        onDelete={async () => {
          await onDelete(teamToDelete!)
          setTeamToDelete(null)
        }}
        onCancel={() => {
          setTeamToDelete(null)
        }}
      />
      <HeaderWrapper>
        <Header>Teams</Header>
        <Subheader mb={3}>
          Create teams below and assign leads. People assigned to a particular
          team will see the default work list item in their area
        </Subheader>
      </HeaderWrapper>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        {teams.map((team, index) => {
          const isFirstItem = index === 0
          const teamSavedData = props.teams.find(
            (serverTeam) => serverTeam.id === team.id,
          )
          function areArraysEqual(
            arr1: WorkQueuesRead[],
            arr2: WorkQueuesRead[],
          ) {
            if (arr1.length !== arr2.length) {
              return false
            }

            for (let i = 0; i < arr1.length; i++) {
              if (arr1[i].id !== arr2[i].id) {
                return false
              }
            }

            return true
          }

          const isSaveButtonEnabled =
            teamSavedData &&
            (team.name !== teamSavedData.name ||
              team.team_lead.id !== teamSavedData.team_lead.id ||
              !areArraysEqual(teamSavedData.workqueues, team.workqueues))
          return (
            <Box
              key={team.id}
              sx={{ display: "flex", gap: "8px", alignItems: "center" }}
            >
              <TextField
                sx={{ width: "50%" }}
                label={isFirstItem ? "Team Name" : undefined}
                value={team.name}
                onChange={({ target: { value } }) => {
                  updateTeam(team, { name: value })
                }}
                disabled={LGInternalUser}
              />

              <FormControl
                sx={{
                  width: "25%",
                }}
              >
                <InputLabel
                  id="team-lead"
                  sx={isFirstItem ? undefined : { display: "none" }}
                >
                  Team Lead
                </InputLabel>
                <Select
                  label={isFirstItem ? "Team Lead" : undefined}
                  labelId="team-lead"
                  value={teamLeads.length === 0 ? "" :  team.team_lead.id }
                  onChange={({ target: { value } }) => {
                    updateTeam(team, { team_lead: { id: value } })
                  }}
                  disabled={LGInternalUser}
                >
                  {teamLeads.map((teamLead) => (
                    <MenuItem key={teamLead.id} value={teamLead.id}>
                      {teamLead.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ width: "25%" }}>
                <InputLabel
                  id="work-lists"
                  sx={isFirstItem ? undefined : { display: "none" }}
                >
                  Worklists
                </InputLabel>
                <Select
                  label={isFirstItem ? "Worklists" : undefined}
                  labelId="work-lists"
                  value={team.workqueues.map((queue) => queue.id)}
                  multiple
                  onChange={(event) => handleTeamSelection(team, event)}
                  disabled={LGInternalUser}
                >
                  {workLists.map((workList) => (
                    <MenuItem key={workList.id} value={workList.id}>
                      {workList.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <PriButton
                variant="outlined"
                color= "secondary"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  // Don't show modal for teams only in memory
                  if (clientOnlyTeams[team.id]) {
                    deleteTeam(team)
                    return
                  }

                  setTeamToDelete(team)
                }}
                sx={{width: '40px', pl: '24px',}}
                disabled={LGInternalUser}
              >
              </PriButton>

              {clientOnlyTeams[team.id] ? (
                <Button
                  sx={{ width: "90px" }}
                  disabled={
                    !team.name || !team.team_lead.id || !team.workqueues || LGInternalUser
                  }
                  label="Submit"
                  onClick={() => {
                    onCreate(team)
                  }}
                />
              ) : (
                <Button
                  sx={{
                    width: "90px",
                    ...(!isSaveButtonEnabled && { visibility: "hidden" }),
                  }}
                  disabled={!isSaveButtonEnabled || LGInternalUser}
                  label="Save"
                  onClick={() => {
                    handleUpdate(team)
                  }}
                />
              )}
            </Box>
          )
        })}
      </Box>
      <PriButton
        onClick={addTeam}
        variant="outlined"
        startIcon={<Add />}
        disabled={teams.some((team) => clientOnlyTeams[team.id]) || LGInternalUser}
        sx={{
        '& .MuiButton-startIcon': {
            mr: .5
        },
        width: 130,
      }}
      >
          Add Team
      </PriButton>
    </Wrapper>
  )
}
